import { atom, useAtomValue } from 'jotai';
import type { Authentication } from '@studio/types';
import { globalStore } from '../store';

/**
 * Session core. Kept in `globalStore` so it survives `Store.reset()`
 * (login / logout / org switch). Atoms are private to this file: read them via
 * `useSession()`, change them via the `session` accessors.
 */

const PRIVATE_accessTokenAtom = atom<string | undefined>(undefined);
const PRIVATE_accessTokenTimestampAtom = atom<number>(0);
const PRIVATE_sessionUserAtom = atom<Authentication.Derived.User | null>(null);
const PRIVATE_tokenExpiredAtom = atom<boolean>(false);
const PRIVATE_isLoggingInAtom = atom<boolean>(false);
const PRIVATE_isRefreshingAtom = atom<boolean>(false);
const PRIVATE_isVenueAtom = atom<boolean>(false);

export type UseSessionResult = {
  accessToken: string | undefined;
  capabilities: string[] | undefined;
  customerId: string | undefined;
  customerName: string | undefined;
  isAuthenticated: boolean;
  isLoggingIn: boolean;
  isRefreshing: boolean;
  isVenue: boolean;
  toggles: string[] | undefined;
  tokenExpired: boolean;
  user: Authentication.Derived.User | null;
  userId: string | undefined;
  userImage: string | undefined;
  userName: string | undefined;
};

export const useSession = (): UseSessionResult => {
  const accessToken = useAtomValue(PRIVATE_accessTokenAtom, { store: globalStore });
  const user = useAtomValue(PRIVATE_sessionUserAtom, { store: globalStore });
  const tokenExpired = useAtomValue(PRIVATE_tokenExpiredAtom, { store: globalStore });
  const isLoggingIn = useAtomValue(PRIVATE_isLoggingInAtom, { store: globalStore });
  const isRefreshing = useAtomValue(PRIVATE_isRefreshingAtom, { store: globalStore });
  const isVenue = useAtomValue(PRIVATE_isVenueAtom, { store: globalStore });

  return {
    accessToken,
    capabilities: user?.capabilities,
    customerId: user?.customerId,
    customerName: user?.customerName,
    isAuthenticated: user != null,
    isLoggingIn,
    isRefreshing,
    isVenue,
    toggles: user?.toggles,
    tokenExpired,
    user,
    userId: user?.userId,
    userImage: user?.userImage,
    userName: user?.userName,
  };
};

/**
 * Session accessors — for module scope (axios 401 interceptor, admin sagas) and the
 * login / logout / refresh flows.
 */
export const session = {
  getAccessToken: (): string => globalStore.get(PRIVATE_accessTokenAtom) ?? '',
  setAccessToken: (token: string | undefined): void => globalStore.set(PRIVATE_accessTokenAtom, token),
  getAccessTokenTimestamp: (): number => globalStore.get(PRIVATE_accessTokenTimestampAtom),
  setAccessTokenTimestamp: (timestamp: number): void => globalStore.set(PRIVATE_accessTokenTimestampAtom, timestamp),
  getUser: (): Authentication.Derived.User | null => globalStore.get(PRIVATE_sessionUserAtom),
  setUser: (user: Authentication.Derived.User | null): void => globalStore.set(PRIVATE_sessionUserAtom, user),
  getCustomerId: (): string | undefined => globalStore.get(PRIVATE_sessionUserAtom)?.customerId,
  isTokenExpired: (): boolean => globalStore.get(PRIVATE_tokenExpiredAtom),
  setTokenExpired: (expired = true): void => globalStore.set(PRIVATE_tokenExpiredAtom, expired),
  setIsRefreshing: (value: boolean): void => globalStore.set(PRIVATE_isRefreshingAtom, value),
  setIsLoggingIn: (value: boolean): void => globalStore.set(PRIVATE_isLoggingInAtom, value),
  getIsLoggingIn: (): boolean => globalStore.get(PRIVATE_isLoggingInAtom),
  getIsRefreshing: (): boolean => globalStore.get(PRIVATE_isRefreshingAtom),
  getIsVenue: (): boolean => globalStore.get(PRIVATE_isVenueAtom),
  setIsVenue: (value: boolean): void => globalStore.set(PRIVATE_isVenueAtom, value),
  // Clear credential + identity on logout (distinct from Store.reset(), which wipes the provider
  // store — logout does both). Preserves `tokenExpired` and `isVenue` for the login.
  clear: (): void => {
    globalStore.set(PRIVATE_accessTokenAtom, undefined);
    globalStore.set(PRIVATE_accessTokenTimestampAtom, 0);
    globalStore.set(PRIVATE_sessionUserAtom, null);
    globalStore.set(PRIVATE_isRefreshingAtom, false);
    globalStore.set(PRIVATE_isLoggingInAtom, false);
  },
};
